import { includes } from 'lodash';
import { get } from 'lodash';
import React from 'react';
import { compose } from 'redux';
import { REDUCER_NAME as COMMON_REDUCER_NAME } from '../../../app/commonReducer';
import { connect } from 'react-redux';
import { withComponentId } from '../../../common/ComponentIdContext';
import { withComponentName } from '../../../common/ComponentNameContext';
import { TYPE_DISABLE } from '../../../common/sync-component/Const';
import { cellNegtiveRed } from '../../../common/table/CustomCellTable';
import EventEmitter, {
  SYNC_COMPONENT_SEARCH,
} from '../../../helpers/EventEmitter';
import {
  formatPrice,
  formatTextFloat,
  formatValueBillion,
  formatVolume,
  formatValue,
} from '../../../helpers/Text';
import { I18n } from 'react-redux-i18n';
import Table from '../../../common/table/Table';
import { getColorPrice } from '../../../helpers/Color';
import {
  getDataFollowKeyByDot,
  getRanking,
  syncComponentSelector,
} from '../../../helpers/Common';
import CellHover from '../../watchlist/summary/CellHover';
import TextBlink from '../../../common/table/TextBlink';
import TradingViewUrl from '../../../common/TradingViewUrl';
import SectorName from './SectorName';

const getClassName = (item, listColumn) => {
  return getColorPrice(
    getDataFollowKeyByDot(item, listColumn.PRICE),
    getDataFollowKeyByDot(item, listColumn.REF_PRICE),
    getDataFollowKeyByDot(item, listColumn.HIGH_PRICE),
    getDataFollowKeyByDot(item, listColumn.LOW_PRICE),
    'text',
  );
};

const getForeignData = (item, listColumn, key) => {
  const buyVol = getDataFollowKeyByDot(item, listColumn.FOREIGN_BUY_VOL);
  const sellVol = getDataFollowKeyByDot(item, listColumn.FOREIGN_SELL_VOL);
  const buyVal = getDataFollowKeyByDot(item, listColumn.FOREIGN_BUY_VALUE);
  const sellVal = getDataFollowKeyByDot(item, listColumn.FOREIGN_SELL_VALUE);

  let result;
  if (key === listColumn.FOREIGN_BUY_VOL) {
    result = buyVol - sellVol;
  }
  if (key === listColumn.FOREIGN_SELL_VOL) {
    result = sellVol - buyVol;
  }
  if (key === listColumn.FOREIGN_SELL_VALUE) {
    return formatTextFloat(formatValue(sellVal - buyVal));
  }
  if (key === listColumn.FOREIGN_BUY_VALUE) {
    return formatTextFloat(formatValue(buyVal - sellVal));
  }

  return formatTextFloat(formatVolume(result));
};

const schema = (listColumn, i18nTitleKey) =>
  Object.keys(listColumn).map((item, index) => {
    const key = listColumn[item];
    const title = `${i18nTitleKey}.${item}`;
    const result = {
      key,
      title,
    };

    if (index === 0) {
      return {
        ...result,
        isString: true,
        render: (text, item) => (
          <CellHover organCode={item.organCode} isShowDeleteIcon={false}>
            <TextBlink item={item} tradingDate="tradingDate">
              <TradingViewUrl code={item.organCode} />
              &nbsp;{text}
            </TextBlink>
          </CellHover>
        ),
      };
    }

    if (includes([listColumn.VOLUME, listColumn.AVG_VOLUME], key)) {
      return {
        ...result,
        render: (text, item) => {
          return (
            <TextBlink item={item} tradingDate="tradingDate">
              {formatTextFloat(formatVolume(text))}
            </TextBlink>
          );
        },
      };
    }

    if (
      includes(
        [
          listColumn.FOREIGN_BUY_VOL,
          listColumn.FOREIGN_BUY_VALUE,
          listColumn.FOREIGN_SELL_VOL,
          listColumn.FOREIGN_SELL_VALUE,
        ],
        key,
      )
    ) {
      return {
        ...result,
        valueFromItem: item => getForeignData(item, listColumn, key),
        render: (text, item) => {
          return (
            <TextBlink item={item} tradingDate="tradingDate">
              {getForeignData(item, listColumn, key)}
            </TextBlink>
          );
        },
      };
    }

    if (includes([listColumn.VALUE], key)) {
      return {
        ...result,
        render: (text, item) => {
          return (
            <TextBlink item={item} tradingDate="tradingDate">
              {formatTextFloat(formatValue(text))}
            </TextBlink>
          );
        },
      };
    }

    if (includes([listColumn.PRICE, listColumn.CHANGE_PRICE], key)) {
      return {
        ...result,
        render: (text, item) => {
          const className = getClassName(item, listColumn);
          return (
            <TextBlink
              item={item}
              tradingDate="tradingDate"
              className={className}
            >
              {formatTextFloat(formatPrice(text))}
            </TextBlink>
          );
        },
      };
    }

    if (includes([listColumn.PERCENT_PRICE_CHANGE], key)) {
      return {
        ...result,
        render: (text, item) => {
          const className = getClassName(item, listColumn);
          return (
            <TextBlink
              item={item}
              tradingDate="tradingDate"
              className={className}
            >
              {formatTextFloat(text * 100)}%
            </TextBlink>
          );
        },
      };
    }

    if (includes([listColumn.OLD_LOW, listColumn.OLD_HIGH], key)) {
      return {
        ...result,
        render: (text, item) => {
          return (
            <TextBlink item={item} tradingDate="tradingDate">
              <span>{formatTextFloat(formatPrice(text))}</span>
            </TextBlink>
          );
        },
      };
    }

    if (includes([listColumn.RTD11], key)) {
      return {
        ...result,
        render: (text, item) => {
          return (
            <TextBlink item={item} tradingDate="tradingDate">
              {formatTextFloat(formatValueBillion(text))}
            </TextBlink>
          );
        },
      };
    }

    if (
      includes(
        [listColumn.RTD21, listColumn.PB, listColumn.BETA, listColumn.PE],
        key,
      )
    ) {
      return {
        ...result,
        render: (text, item) => {
          return (
            <TextBlink item={item} tradingDate="tradingDate">
              {cellNegtiveRed(text, 2, true)}
            </TextBlink>
          );
        },
      };
    }

    if (includes([listColumn.SECTOR_NAME], key)) {
      return {
        ...result,
        isString: true,
        thStyle: { textAlign: 'left' },
        render: (text, item, width) => {
          return <SectorName width={width}>{text}</SectorName>;
        },
      };
    }

    if (result.key === listColumn.RANKING) {
      return {
        ...result,
        disableResize: true,
        thTooltip: I18n.t('table.topMover.ranking'),
        render: (rank, item) => {
          const totalRank = getDataFollowKeyByDot(item, listColumn.TOTAL_RANK);

          return (
            <div className="pl-8 pr-8"> {getRanking(rank, totalRank)}</div>
          );
        },
      };
    }

    return {
      ...result,
      render: text => text,
    };
  });

class TableOverview extends React.PureComponent {
  onRowClick = organCode => {
    const { listOrganizationByOrganCode, syncType } = this.props;
    if (syncType === TYPE_DISABLE) {
      return;
    }
    EventEmitter.dispatch(SYNC_COMPONENT_SEARCH, {
      syncType: syncType,
      search: listOrganizationByOrganCode[organCode],
    });
  };

  render() {
    const {
      listId,
      getDataFromRedux,
      hideColumns,
      listColumns,
      resizable,
      columnDraggable,
      rowDraggable,
      i18nTitleKey,
      isLoading,
      table,
      sortState,
      onSort,
    } = this.props;

    return (
      <Table
        clickRow={this.onRowClick}
        thValign="top"
        sortState={sortState}
        onSort={onSort}
        isLoading={isLoading}
        ids={listId}
        table={table}
        resizable={resizable}
        columnDraggable={columnDraggable}
        rowDraggable={rowDraggable}
        hideColumns={hideColumns}
        getDataFromRedux={getDataFromRedux}
        schema={schema(listColumns, i18nTitleKey)}
      />
    );
  }
}

const mapStateToProps = (state, { componentName, componentId }) => {
  return {
    listOrganizationByOrganCode:
      state[COMMON_REDUCER_NAME].listOrganizationByOrganCode,
    syncType: syncComponentSelector(state, componentName, componentId),
  };
};

export default compose(
  withComponentId,
  withComponentName,
  connect(mapStateToProps),
)(TableOverview);
